/* ==========================================================================
   Xenon filter chip bar — dropdown behaviour.

   The chips are the theme's own <details is="accordion-group"> elements, so
   opening/closing is native. Two things the theme's accordion cannot give a
   dropdown, both of which Hydrogen's FilterDropdown has:

     1. Only one panel open at a time. AccordionGroup.handleAfterToggle is
        supposed to do this, but it closes siblings via `details.open = false`,
        which routes into AccordionDetails.animateAccordion — an async height
        animation that this layout deliberately neutralises with
        `height: auto !important` (the panel is absolutely positioned, so an
        animated height on the <details> is meaningless and its inline
        `overflow: hidden` clips the dropdown). The close therefore never
        lands. Closing is done here instead, synchronously.
     2. Dismiss on outside click / Escape. A dropdown that can only be closed
        by re-clicking the chip it now covers is a trap.

   Listeners are delegated from `document` on purpose: assets/facets.js
   replaces #ProductGridContainer.innerHTML on every filter change, and the
   toolbar is re-rendered with it, so anything bound per-element would go dead
   after the first filter.
   ========================================================================== */
(function () {
  'use strict';

  var BAR = '.xfacet-bar';
  var CHIP = 'details.facet-accordion-details';

  /* Close without going through AccordionDetails' async animation. isOpen is
     the element's own state field; leaving it stale would make the next click
     compute the wrong toggle and need two taps. */
  function close(details) {
    if (!details.hasAttribute('open')) return;
    details.removeAttribute('open');
    details.isOpen = false;
    details.setAttribute('aria-expanded', 'false');
    // animateAccordion may have left these behind mid-flight.
    details.style.removeProperty('height');
    details.style.removeProperty('overflow');
  }

  function closeAll(bar, except) {
    Array.prototype.forEach.call(bar.querySelectorAll(CHIP), function (d) {
      if (d !== except) close(d);
      else align(d);
    });
  }

  /* --------------------------------------------------------------------
     Which way the panel opens.

     A panel is anchored to its chip's LEFT by default and flipped to the
     right when that would push it past the toolbar. CSS cannot decide this:
     the bar wraps, so a chip's position in the row is not its position in
     the markup — the structural `:nth-last-child(-n + 2)` rule this replaces
     right-anchored the last two chips in DOM order, which on a wrapped bar
     are the FIRST chips of the bottom row, and their panels then ran off the
     left edge of the page.

     Measured live rather than from a width table: the panel sizes itself to
     its longest option label (min-width 24rem, max-width 34rem), the chips
     size themselves to their facet names, and the bar re-wraps at every
     breakpoint and after every filter.
     -------------------------------------------------------------------- */
  var EDGE_GUTTER = 8;

  function align(details) {
    if (!details || !details.hasAttribute('open')) return;
    var panel = details.querySelector('.accordion-details__content');
    if (!panel) return;

    /* Measure the natural, left-anchored position — the attribute from a
       previous open would otherwise be measured instead of the default. */
    details.removeAttribute('data-align');

    var limit = window.innerWidth - EDGE_GUTTER;
    var toolbar = details.closest('.collection__toolbar');
    if (toolbar) {
      var box = toolbar.getBoundingClientRect();
      var pad = parseFloat(getComputedStyle(toolbar).paddingRight) || 0;
      limit = Math.min(limit, box.right - pad);
    }

    if (panel.getBoundingClientRect().right > limit) {
      details.setAttribute('data-align', 'end');
    }
  }

  document.addEventListener(
    'click',
    function (event) {
      var bar = document.querySelector(BAR);
      if (!bar) return;

      var summary = event.target.closest ? event.target.closest(BAR + ' summary') : null;
      if (summary) {
        var mine = summary.closest(CHIP);
        /* The theme's own handler toggles on the same click, so wait a frame
           and then reconcile — otherwise this races it. */
        requestAnimationFrame(function () {
          closeAll(bar, mine);
        });
        return;
      }

      /* A click inside an open panel (ticking a checkbox) must not close it —
         multi-select stays open, as it does in Hydrogen's drawer. */
      if (!event.target.closest || !event.target.closest(BAR)) {
        closeAll(bar, null);
      }
    },
    false
  );

  /* --------------------------------------------------------------------
     Stuck loading state on the "Filtreler" button.

     facets.js:201 beforeRenderSection() adds `btn--loading` inside a 100ms
     setTimeout, while afterRenderSection() removes it as soon as the section
     lands. A response served from facets.js's own cachedMap arrives in well
     under 100ms, so the add fires AFTER the remove and the class sticks —
     `.btn--loading` fades .btn__text to 0, which is why the tile renders
     empty until the next slow request.

     Pre-existing, but invisible while filtering was disabled: the only
     [data-facet-loading] element was the product-count paragraph.

     Cleared one tick past that 100ms window, on the theme's own
     re-render event.
     -------------------------------------------------------------------- */
  document.addEventListener('collection:rerendered', function () {
    setTimeout(function () {
      Array.prototype.forEach.call(document.querySelectorAll('[data-facet-loading]'), function (el) {
        el.classList.remove('btn--loading');
      });
    }, 150);
  });

  /* The bar re-wraps as the window changes width, which can move an open chip
     from the middle of a row to its right edge. */
  window.addEventListener('resize', function () {
    var bar = document.querySelector(BAR);
    if (!bar) return;
    align(bar.querySelector(CHIP + '[open]'));
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var bar = document.querySelector(BAR);
    if (!bar) return;
    var open = bar.querySelector(CHIP + '[open]');
    if (!open) return;
    close(open);
    var summary = open.querySelector('summary');
    if (summary) summary.focus();
  });
})();
